import React, { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { arrayOf, bool, func, shape } from 'prop-types';

// Utils
import { sidebarStyles } from 'feature/loading-management/utils/const';
import { mapLocationData, mapStates } from 'feature/loading-management/utils/timeHelpers';

// Components
import { Box, useMediaQuery } from '@mui/material';
import SelectorsList from 'feature/loading-management/selectors-list/SelectorsList';
import SelectorVirtualizer from 'feature/loading-management/selectors-list/SelectorVirtualizer';
import { styled, useTheme } from '@mui/material/styles';
import SelectorSkeleton from './selector-skeleton/SelectorSkeleton';
import Filters from './Filters.connector';
import ListLabel from './list-label/ListLabel.connector';
import Selector from './selector/Selector.connector';

const SelectorsListStyled = styled(SelectorsList)(({ theme }) => ({
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  height: '100%',
  overflowY: 'scroll',
  padding: theme.spacing(0, 0, 4.5),
  scrollbarWidth: 'none',
}));

const sideBar = {
  ...sidebarStyles, // eslint-disable-next-line
  width: { xs: 1, sm: 1, md: 420, lg: 420 },
};

const Sidebar = ({
  fetchActiveTerminals,
  fetchWaitTimes,
  locations,
  locationsPending,
  nonFilteredLocations,
  setActiveLocation,
  setLocations,
  setStates,
}) => {
  const theme = useTheme();
  const { viewById } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const mdUp = useMediaQuery(theme.breakpoints.up('md'), { noSsr: true });

  const adjustedViewById = decodeURI(viewById || '');

  // Timeout so that every 30 min the page is reloaded to fetch new data
  setTimeout(() => {
    window.location.reload();
    // 30 min in milliseconds = 1800000
  }, 1800000);

  // This use effect must stay here: due to the
  // size of the selector on mobile vs desktop in RWT
  // when resizing the virtualizer does not rerender (virtualized...)
  // and we have to force some sort of change to rerender
  // the selectors to the appropriate height based
  // on the users browser window width
  useEffect(() => {
    if (locations.length) {
      setLocations(locations);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mdUp]);

  useEffect(() => {
    const fetchData = async () => {
      const fetchSuccessArr = await Promise.all([fetchActiveTerminals(), fetchWaitTimes()]);
      setStates(mapStates(fetchSuccessArr[0].data));
      setLocations(mapLocationData(fetchSuccessArr).filter(f => f.isTerminalLoading));
    };

    // eslint-disable-next-line no-console
    fetchData().catch(err => console.warn('error fetching data', err));
  }, [fetchActiveTerminals, fetchWaitTimes, setLocations, setStates]);

  useEffect(() => {
    if (nonFilteredLocations.length) {
      if (viewById) {
        const found =
          nonFilteredLocations.find(({ nameShort }) => nameShort === adjustedViewById) || {};
        setActiveLocation(found);
      } else {
        setActiveLocation(nonFilteredLocations[0]);
        navigate(`${pathname}/${nonFilteredLocations[0].nameShort}`, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adjustedViewById, viewById, nonFilteredLocations.length]);

  const getItemSize = () => (mdUp ? 65 : 90);

  return (
    <Box {...sideBar}>
      <Filters />
      {locationsPending ? <SelectorSkeleton /> : <ListLabel />}
      <SelectorsListStyled
        loading={locationsPending}
        selector={
          <SelectorVirtualizer
            itemSize={getItemSize}
            overscanCount={10}
            selectorComponent={Selector}
            selectorsData={locations}
          />
        }
        skeletonComponent={<SelectorSkeleton />}
        skeletonHeight={mdUp ? 65 : 90}
      />
    </Box>
  );
};

Sidebar.propTypes = {
  fetchActiveTerminals: func.isRequired,
  fetchWaitTimes: func.isRequired,
  locations: arrayOf(shape({})).isRequired,
  locationsPending: bool.isRequired,
  nonFilteredLocations: arrayOf(shape({})).isRequired,
  setActiveLocation: func.isRequired,
  setLocations: func.isRequired,
  setStates: func.isRequired,
};

export default Sidebar;
